import { ApolloError } from '@apollo/client';
import {
  SLFAnalysisFragment,
  StatusQuoAnalysisBuildingBasicDetailsFragment,
  StatusQuoAnalysisBuildingGetBuildingQuery,
} from '@predium/client-graphql';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelectedReportingYear } from '@/sections/StatusQuoAnalysis/hooks/useSelectedReportingYear';
import { useQueryGetAnalyzedBuilding } from '@/sections/StatusQuoAnalysis/queries/useQueryGetAnalyzedBuilding';
import { useQueryGetBenchmark } from '@/sections/StatusQuoAnalysis/queries/useQueryGetBenchmark';
import { useQueryGetSingleBuilding } from '@/sections/StatusQuoAnalysis/queries/useQueryGetSingleBuilding';
import { FloorAreaByClassOfUse } from '@/sections/StatusQuoAnalysis/utils';
import {
  StatusQuoAnalysisTabAndBenchmarkContextProvider,
  useStatusQuoAnalysisTabAndBenchmarkContext,
} from './StatusQuoAnalysisTabAndBenchmarkContext';
import { StatusQuoViewFiltersInitialValues, StatusQuoViewFiltersProvider } from './StatusQuoViewFiltersContext';

type AnalyzedBuilding = StatusQuoAnalysisBuildingGetBuildingQuery['getAnalyzedBuilding'];

export type StatusQuoAnalysisBuildingContextType = {
  selectedBuilding: StatusQuoAnalysisBuildingBasicDetailsFragment | undefined;
  isLoadingBuilding: boolean;
  buildingError: ApolloError | undefined;
  analyzedBuilding: AnalyzedBuilding | undefined;
  isLoadingAnalyzedBuilding: boolean;
  benchmark: ReturnType<typeof useQueryGetBenchmark>['benchmark'];
  isLoadingBenchmark: boolean;
  slfAnalysis: SLFAnalysisFragment | undefined;
  floorAreaByBuildingClass: FloorAreaByClassOfUse;
  selectedReportingYear: number;
  setSelectedReportingYear: (year: number) => void;
};

const initialContext: StatusQuoAnalysisBuildingContextType = {
  selectedBuilding: undefined,
  isLoadingBuilding: false,
  buildingError: undefined,
  analyzedBuilding: undefined,
  isLoadingAnalyzedBuilding: false,
  benchmark: undefined,
  isLoadingBenchmark: false,
  slfAnalysis: undefined,
  floorAreaByBuildingClass: [],
  selectedReportingYear: 2020,
  setSelectedReportingYear: () => {},
};

const StatusQuoAnalysisBuildingContext = createContext<StatusQuoAnalysisBuildingContextType>(initialContext);

/**
 * Inner provider that runs inside StatusQuoAnalysisTabAndBenchmarkContextProvider so it can
 * read activeBenchmark from the tab/benchmark context.
 */
function StatusQuoAnalysisBuildingContextInner({ children }: PropsWithChildren) {
  const { id } = useParams<{ id: string }>();
  const buildingId = Number(id);

  const { benchmarkPortfolioId, isBenchmarkActive } = useStatusQuoAnalysisTabAndBenchmarkContext();

  const { selectedBuilding, isLoadingBuilding, buildingError } = useQueryGetSingleBuilding();

  const { analyzedBuilding, isLoadingAnalyzedBuilding } = useQueryGetAnalyzedBuilding({
    buildingId,
  });

  const { benchmark, isLoadingBenchmark } = useQueryGetBenchmark({ benchmarkPortfolioId, isBenchmarkActive });

  const { selectedReportingYear, setSelectedReportingYear } = useSelectedReportingYear();

  const slfAnalysis = useMemo(() => analyzedBuilding?.slfAnalysis, [analyzedBuilding]);
  const floorAreaByBuildingClass = useMemo<FloorAreaByClassOfUse>(
    () => analyzedBuilding?.floorAreaByClassOfUse ?? [],
    [analyzedBuilding],
  );

  return (
    <StatusQuoAnalysisBuildingContext.Provider
      value={{
        selectedBuilding,
        isLoadingBuilding: isLoadingBuilding,
        buildingError,
        analyzedBuilding,
        isLoadingAnalyzedBuilding,
        benchmark,
        isLoadingBenchmark,
        slfAnalysis,
        floorAreaByBuildingClass,
        selectedReportingYear,
        setSelectedReportingYear,
      }}
    >
      {children}
    </StatusQuoAnalysisBuildingContext.Provider>
  );
}

/**
 * Context provider for the normal (non-shared) building status quo analysis page.
 * Wraps StatusQuoAnalysisTabAndBenchmarkContextProvider internally and adds
 * building-specific query results.
 */
export function StatusQuoAnalysisBuildingContextProvider({ children }: PropsWithChildren) {
  return (
    <StatusQuoAnalysisTabAndBenchmarkContextProvider>
      <StatusQuoViewFiltersProvider>
        <StatusQuoAnalysisBuildingContextInner>{children}</StatusQuoAnalysisBuildingContextInner>
      </StatusQuoViewFiltersProvider>
    </StatusQuoAnalysisTabAndBenchmarkContextProvider>
  );
}

/**
 * Provider for the shared (snapshot) building view. Accepts preloaded data and exposes it
 * through the same context interface so child components work identically.
 */
type SharedProviderProps = PropsWithChildren<{
  selectedBuilding: StatusQuoAnalysisBuildingBasicDetailsFragment | undefined;
  analyzedBuilding: AnalyzedBuilding | undefined;
  benchmark?: StatusQuoAnalysisBuildingContextType['benchmark'];
  /**
   * Initial reporting year used by the Consumption tab. Comes from the snapshot's
   * exportConfig so the rendered PDF matches the year the user picked at export time.
   */
  initialReportingYear?: number;
  /**
   * Frozen view-filter selections from the snapshot's exportConfig.viewFilters.
   * Seeded into the StatusQuoViewFiltersProvider so widgets render with the
   * user's chosen sub-tab / perspective / discount rate.
   */
  initialViewFilters?: StatusQuoViewFiltersInitialValues;
}>;

export function SharedStatusQuoAnalysisBuildingContextProvider({
  selectedBuilding,
  analyzedBuilding,
  benchmark,
  initialReportingYear,
  initialViewFilters,
  children,
}: SharedProviderProps) {
  const [selectedReportingYear, setSelectedReportingYear] = useState<number>(
    initialReportingYear ?? new Date().getFullYear(),
  );

  const slfAnalysis = useMemo(() => analyzedBuilding?.slfAnalysis, [analyzedBuilding]);
  const floorAreaByBuildingClass = useMemo<FloorAreaByClassOfUse>(
    () => analyzedBuilding?.floorAreaByClassOfUse ?? [],
    [analyzedBuilding],
  );

  return (
    <StatusQuoViewFiltersProvider initialValues={initialViewFilters}>
      <StatusQuoAnalysisBuildingContext.Provider
        value={{
          selectedBuilding,
          isLoadingBuilding: false,
          buildingError: undefined,
          analyzedBuilding,
          isLoadingAnalyzedBuilding: false,
          benchmark,
          isLoadingBenchmark: false,
          slfAnalysis,
          floorAreaByBuildingClass,
          selectedReportingYear,
          setSelectedReportingYear,
        }}
      >
        {children}
      </StatusQuoAnalysisBuildingContext.Provider>
    </StatusQuoViewFiltersProvider>
  );
}

/**
 * Hook to consume the building context.
 */
export function useStatusQuoAnalysisBuildingContext() {
  return useContext(StatusQuoAnalysisBuildingContext);
}
